import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Order Status type
export type OrderStatus = 'In Process' | 'Expired' | 'Product Verified' | 'QR Activated' | 'Verification Pending' | 'Failed' | 'Approved' | 'Completed' | 'Cancelled';

export interface OrderDocument {
  id: string;
  type: 'invoice' | 'customerImage' | 'other';
  label: string;
  fileUrl: string;
  uploadDate: string;
  size?: number;
}

// Order interface matching the API response
export interface Order {
  _id: string;
  orderId: string;
  utr: string | null;
  utrReason: string | null;
  utrBy: string | null;
  utrUpdatedAt: string | null;
  status: OrderStatus;
  lender: string;
  merchantId: string | null;
  customerId?: string;
  partnerId?: string;
  category?: string;
  subCategory?: string;
  orderCreatedAt?: number;
  createdAt: string;
  updatedAt: string;
  transaction?: {
    eligibleTenure: any[];
    transactionId?: string | null;
    transactionStatus?: string | boolean;
    transactionTime?: number;
    leafRefNo?: string;
  };
  settlement?: {
    customerName: string;
    customerMobileNo: string;
    productName: string;
    productVariation: string;
    productDetails: string;
    productBrand: string;
    merchantName: string;
    merchantMobileNo: string;
    merchantEmail: string;
    partnerName: string;
    utr: string | null;
    utrAt: string | null;
    utrReason: string | null;
    utrBy : string | null;
    orderPaymentAt: string | null;
    orderId: string;
    orderCreationAt: string;
    orderLinkAt: string;
    schemeName: string;
    schemeDownPayment: number;
    schemeTenure: number;
    schemeEMIAmount: number;
    paymentFrequency: string;
    netDisbursement: number;
    totalAmount: number;
    interestAmount: number;
  };
  validation?: number;
  serialNo?: string;
  paymentLink?: string;
  redirectionLink?: string;
  docProduct?: string;
  docReceipt?: string;
  docProductAt?: string;
  docReceiptAt?: string;
  docCustomerConsent?: string;
  docCustomerConsentAt?: string;
  docVehicleInvoice?: string;
  docVehicleInvoiceAt?: string;
  docVehicleMotor?: string;
  docVehicleMotorAt?: string;
  docInsurance?: string;
  docInsuranceAt?: string;
  docRtoAcknowledgement?: string;
  docRtoAcknowledgementAt?: string;
  customDocument?: string;
  customDocumentAt?: string;
  customDocumentName?: string;
  serialNumber?: number;
  amount?: number;
  linkActivatedAt?: number;
  logs?: string[]; // Logs
  keyFactStatement?: {
    amount: number;
    scheme: {
      name: string;
      grossTenure: number;
      advancedEMI: number;
      subventionCost: number;
      interestBased: boolean;
      annualInterest: number;
      otherCharges: number;
      totalProcessingFee: number;
      GST: number;
    };
    calculations: {
      name: string;
      grossTenure: number;
      NoOfNetEmis: number;
      netDisbursement: number;
      totalInterest: number;
      netLoanAmount: number;
      monthlyEMI: number;
      totalInterestPayable: number;
      advancedEmiPayment: number;
      partnerTotalDownPayment: number;
      totalProcessingFee: number;
      DBDToDealer: number;
      totalDownPayment: number;
      merchantGets: number;
      netSettlementByLenderToPA: number;
      netSettlementToMerchantFromPA: number;
      merchantTotalDeduction: number;
      merchantSubCost: number;
    };
  };
  availableSchemes?: any[];
}

export interface GenerateLinkPayload {
  amount: number;
  mobileNumber: number;
  name: string;
  email: string;
  pan: string;
  dob: string;
  employmentType: string;
  declaredIncome: number;
  callbackUrl: string;
  productDetails: string;
  merchantId: string;
  pincode: number;
}

export const generateTxnLink = async (payload: GenerateLinkPayload, partnerId: string) => {
  const url = 'https://prod-native-api.cleverpe.com/api/partner/order/generate-link';
  const adminSecret = import.meta.env.VITE_REACT_APP_ADMIN_BYPASS_SECRET || '';

  console.log('[generateTxnLink] request:', {
    url,
    partnerId,
    adminSecretPresent: !!adminSecret,
    adminSecretLength: adminSecret.length,
    adminSecretPreview: adminSecret ? `${adminSecret.slice(0, 3)}...${adminSecret.slice(-2)}` : '(empty)',
    payload,
  });

  console.log('[generateTxnLink] all exposed env keys:', Object.keys(import.meta.env));

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Secret': adminSecret,
      'X-Partner-Id': partnerId,
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));

  console.log('[generateTxnLink] response:', {
    status: res.status,
    ok: res.ok,
    body: json,
  });

  if (!res.ok) throw new Error(json?.message || 'Failed to generate link');
  return json;
};

export interface OrderDetail
  extends Omit<Order, 'merchantId' | 'customerId' | 'partnerId'> {

  /** UI helpers */
  id?: string;
  callbackUrl?: string;
  productDetails?: string;
  
  /** Expanded IDs */
  merchantId?: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    kycBank?: {
      accountNo: string;
      ifsc: string;
      holderName: string;
    };
  };

  customerId?: {
    _id: string;
    name: string;
    mobileNo: string;
    monthlyIncome?: number;
    employmentType?: number;
  };

  partnerId?: {
    _id: string;
    name: string;
    mobileNo: string;
    email: string;
    isEv : boolean;
  };

  /** Product info (UI-only) */
  productId?: {
    _id: string;
    partnerName: string;
    brandName: string;
    name: string;
    category: string;
    variations: {
      name: string;
      mrp: number;
      mop: number;
      features: { name: string; value: string }[];
    }[];
  };

  variationString?: string;

  /** Documents (computed) */
  documents?: OrderDocument[];

  /** Flattened UI helpers */
  product?: {
    name: string;
    variation: string;
    total: number;
    brandName: string;
  };

  customer?: {
    name: string;
    mobileNumber: string;
    email?: string;
    pan?: string;
    dob?: string;
    employmentType?: string | number;
    income?: number;
  };

  merchant?: {
    name: string;
    mobileNumber: string;
    id: string;
    email?: string;
    bankAccountNo?: string;
    bankIfsc?: string;
    bankHolderName?: string;
    paymentAcknowledgment?: boolean;
    grpId?: string;
    gstin?: string;
    contactPerson?: string;
    commission?: number;
  };

  partner?: {
    name: string;
    mobileNumber: string;
    email?: string;
    bankAccountNo?: string;
    bankIfsc?: string;
    bankHolderName?: string;
    contactPerson?: string;
    commission?: number;
  };

  /** Payment summary */
  payment?: {
    totalAmount: number;
    schemeName?: string;
    schemeDownPayment?: number;
    schemeTenure?: number;
    schemeEmiAmount?: number;
    netDisbursement?: number;
    interestAmount?: number;
    paymentFrequency?: string;
    installments?: {
      dueDate: string;
      amount: number;
      status: string;
    }[];
  };
}

// API response interface
export interface OrderApiResponse {
  status: string;
  message?: string;
  result?: {
    pagination: {
      totalCount: number;
      totalMatch: number;
      page: number;
      pageSize: number;
      totalPages: number;
    };
    data: Order[];
  };
}

// Filter interface for orders
export interface OrderFilters {
  status?: OrderStatus | 'all';
  lender?: string;
  brand?: string;
  merchantId?: string;
  customerId?: string;
  dateRange?: {
    from?: string | undefined;
    to?: string | undefined;
  };
}

/**
 * Fetch orders with pagination, sorting, filtering and search
 * @param page Current page number
 * @param pageSize Number of items per page
 * @param sortBy Field to sort by
 * @param sortOrder Sort direction (asc or desc)
 * @param search Search query
 * @param token Authentication token
 * @param filters Additional filters for orders
 * @returns Promise with orders data and pagination info
 */
export const fetchOrders = async ({
  page = 1,
  pageSize = 10,
  sortBy = "createdAt",
  sortOrder = "desc",
  search = "",
  token = "",
  filters = {}
}: {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
  token?: string | null;
  filters?: OrderFilters;
}): Promise<{
  orders: Order[];
  totalMatch: number;
  pageNo: number;
  pageSize: number;
  paginationMessage?: string;
}> => {
  try {
    let authToken = token || localStorage.getItem("token") || "";

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);
    
    // Add filter parameters
    if (filters.merchantId) params.append("merchantId", filters.merchantId);
    if (filters.customerId) params.append("customerId", filters.customerId);
    if (filters.lender) params.append("lender", filters.lender);
    if (filters.brand) params.append("brand", filters.brand);
    
    if (filters.status && filters.status !== 'all') {
      params.append("status", filters.status);
    }
    
    // Date filters use 'from' and 'to' params in DD-MM-YYYY format
    if (filters.dateRange?.from) params.append("from", formatDateForAPI(filters.dateRange.from));
    if (filters.dateRange?.to) params.append("to", formatDateForAPI(filters.dateRange.to));

    console.log(`[API] Fetching orders with filters:`, {
      page,
      pageSize,
      sortBy,
      sortOrder,
      search,
      filters,
      formattedDates: {
        from: filters.dateRange?.from ? formatDateForAPI(filters.dateRange.from) : undefined,
        to: filters.dateRange?.to ? formatDateForAPI(filters.dateRange.to) : undefined
      }
    });
    
    const url = `/adminorder/v1?${params.toString()}`;
    console.log(`[API] Request URL: ${url}`);

    const response = await axios.get<OrderApiResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    console.log("ORDER API RESPONSE:", response.data);

    if (
      response.data?.status !== "Request successful!" ||
      !response.data.result
    ) {
      throw new Error("Unexpected API response structure");
    }

    // Handle the response structure
    const { pagination, data } = response.data.result;
    
    if (!Array.isArray(data)) {
      console.error("Data is not an array:", data);
      throw new Error("Invalid data format in API response");
    }
    
    let orders: Order[] = data;
    let totalMatch = pagination.totalMatch;
    let pageNo = pagination.page;
    let actualPageSize = pagination.pageSize;
    let paginationMessage = `Server-side pagination is active. Total pages: ${pagination.totalPages}`;
    
    // Add serial numbers to orders based on their position in the paginated results
    orders = orders.map((order, index) => {
      const serialNumber = (pageNo - 1) * actualPageSize + index + 1;
      return {
        ...order,
        serialNumber
      };
    });
    
    console.log(`Server pagination: Page ${pageNo} of ${pagination.totalPages}, showing ${orders.length} of ${totalMatch} total orders`);

    return {
      orders,
      totalMatch,
      pageNo,
      pageSize: actualPageSize,
      paginationMessage
    };
  } catch (error: any) {
    console.error("Error fetching orders:", error);
    showToast.error(error.message || 'Failed to fetch orders');
    throw error;
  }
};

// Helper function to format dates for the API
function formatDateForAPI(dateString: string): string {
  try {
    // If the input is already in 'DD-MM-YYYY' format, return as is
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    // If it's in ISO or 'YYYY-MM-DD' format, convert to 'DD-MM-YYYY'
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if not a valid date
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original in case of error
  }
}

/**
 * Fetch a single order by ID
 * @param orderId Order ID (not MongoDB _id)
 * @param token Authentication token
 * @returns Promise with order data
 */
export const fetchOrderById = async (
  orderId: string,
  token: string | null
): Promise<Order> => {
  try {
    console.log(`[API] Fetching order with orderId: ${orderId}`);
    
    const response = await axios.get<{
      status: string;
      result: {
        data: Order | Order[];
      };
    }>(`/adminorder/v1?orderId=${orderId}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || localStorage.getItem("token") || ''
      },
      timeout: 10000 // Setting shorter timeout
    });
    
    console.log('[API] Order detail response:', response.data);
    
    // Handle both array and object response formats
    if (response.data?.status && response.data.result && response.data.result.data) {
      // If data is an array, take the first item
      if (Array.isArray(response.data.result.data)) {
        if (response.data.result.data.length > 0) {
          return response.data.result.data[0];
        } else {
          throw new Error('Order not found');
        }
      } 
      // If data is an object, use it directly
      else {
        return response.data.result.data as Order;
      }
    } else {
      throw new Error(response.data?.status || 'Order not found');
    }
  } catch (error: any) {
    console.error('Error fetching order details:', error);
    showToast.error(error.message || 'Failed to fetch order details');
    throw error;
  }
};

/**
 * Update an order (status, UTR, etc.)
 * @param orderId Order ID (not MongoDB _id)
 * @param updateData Data to update
 * @param token Authentication token
 * @returns Promise with update success status
 */
export const updateOrder = async (
  orderId: string,
  updateData: {
    status?: OrderStatus;
    utr?: string | null;
    utrReason?: string | null;
    txnId?: string;
  },
  token: string | null
): Promise<boolean> => {
  try {
    console.log(`[API] Updating order with orderId: ${orderId}`, updateData);
    
    const authToken = token || localStorage.getItem("token") || "";
    
    // Create the request payload from updateData
    const payload: any = {};
    if (updateData.status) payload.status = updateData.status;
    
    // Make UTR and UTR reason independent - either can be provided without the other
    if ('utr' in updateData) payload.utr = updateData.utr;
    if ('utrReason' in updateData) payload.utrReason = updateData.utrReason;
    if ('txnId' in updateData) payload.txnId = updateData.txnId;
    
    console.log('[API] Update payload:', payload);
    
    const response = await axios.put<{
      status?: string; 
      message?: string;
      result?: {
        order?: {
          status?: string;
          utr?: string;
          utrReason?: string;
          [key: string]: any;
        }
      }
    }>(
      `/adminorder/v1?id=${orderId}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      }
    );
    
    console.log('[API] Order update response:', response.data);
    
    // Consider HTTP 200 as success, regardless of the response body structure
    if (response.status === 200) {
      return true;
    }
    // Additional checks for specific response formats (kept for backward compatibility)
    else if (response.data?.status === "Request successful!" || 
             response.data?.status === "Completed" ||
             response.data?.result?.order?.status) {
      return true;
    } else {
      throw new Error(response.data?.message || 'Failed to update order');
    }
  } catch (error: any) {
    console.error('Error updating order:', error);
    
    // Check if there's a response with data that might contain an actual success message
    if (error.response?.status === 200) {
      console.log('Error with 200 status - treating as success');
      return true;
    } else if (error.response?.data) {
      console.log('Error response data:', error.response.data);
      
      // If the response actually indicates success despite the error
      if (error.response.data.status === "Completed" || 
          error.response.data.status === "Request successful!" ||
          error.response.data.result?.order?.status) {
        return true;
      }
      
      // Show the specific error message if available
      showToast.error(error.response.data.message || 'Failed to update order');
    } else {
      // Generic error message for network errors, etc.
      showToast.error(error.message || 'Failed to update order');
    }
    return false;
  }
};

/**
 * Fetch document URL for product or receipt
 * @param orderId Order ID
 * @param docType Type of document ('docProduct' or 'docReceipt')
 * @param token Authentication token
 * @returns Promise with document URL
 */
export const fetchOrderDocument = async (
  orderId: string,
  docType: 'docProduct' | 'docReceipt' | 'docCustomerConsent' | 'docVehicleChassis' | 'docVehicleMotor' | 'docInsurance' | 'docRtoAcknowledgement' | 'customDocument',
  token: string | null
): Promise<string> => {
  try {
    const authToken = token || localStorage.getItem("token") || "";
    
    const response = await axios.get<{
      status: string;
      result: {
        url: string;
      };
    }>(`/adminorder/v1/${docType}?id=${orderId}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });
    
    if (response.data?.status === "Request successful!" && response.data.result?.url) {
      return response.data.result.url;
    } else {
      throw new Error('Failed to fetch document URL');
    }
  } catch (error: any) {
    console.error(`Error fetching ${docType}:`, error);
    // showToast.error(error.message || `Failed to fetch ${docType}`);
    throw error;
  }
};

/**
 * Delete document (product or receipt)
 * @param orderId Order ID
 * @param docType Type of document ('docProduct' or 'docReceipt')
 * @param token Authentication token
 * @returns Promise with deletion success status
 */
export const deleteOrderDocument = async (
  orderId: string,
  docType: 'docProduct' | 'docReceipt' | 'docCustomerConsent' | 'docVehicleChassis' | 'docVehicleMotor' | 'docInsurance' | 'docRtoAcknowledgement' | 'customDocument',
  token: string | null
): Promise<boolean> => {
  try {
    const authToken = token || localStorage.getItem("token") || "";
    
    const response = await axios.delete<{
      status: string;
      message?: string;
    }>(`/adminorder/v1/${docType}?id=${orderId}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });
    
    if (response.data?.status === "Request successful!") {
      console.log(`${docType} deleted successfully for order ${orderId}`)
      return true;
    } else {
      throw new Error(response.data?.message || `Failed to delete ${docType}`);
    }
  } catch (error: any) {
    console.error(`Error deleting ${docType}:`, error);
    showToast.error(error.message || `Failed to delete ${docType}`);
    return false;
  }
};

